import { useEffect, useMemo, useRef, useState } from "react";
import type { BlockType, EditorConfig } from "./types";

const GROUP_LABELS: Record<string, string> = {
  text: "Text",
  media: "Media",
  embed: "Embeds",
  layout: "Layout",
  special: "Special",
};

const GROUP_ORDER = ["text", "media", "embed", "layout", "special"];

/**
 * Modal overlay (centered card) that lists the available block types grouped
 * by category. Live filter on type name + label. Click or hit Enter to insert.
 */
export function BlockPalette({
  config,
  onPick,
  onClose,
}: {
  config: EditorConfig;
  onPick: (type: BlockType) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = config.paletteOrder
      ? config.paletteOrder
          .map((k) => config.blockTypes.find((t) => t.type === k))
          .filter((t): t is BlockType => !!t)
      : config.blockTypes;
    if (!q) return all;
    return all.filter(
      (t) =>
        t.type.includes(q) ||
        t.label.toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q),
    );
  }, [config, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, BlockType[]>();
    for (const t of filtered) {
      const list = map.get(t.group) ?? [];
      list.push(t);
      map.set(t.group, list);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => [g, map.get(g)!] as const);
  }, [filtered]);

  // Keyboard arrow navigation across the flat filtered list
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = filtered[activeIdx];
      if (pick) onPick(pick);
    }
  };

  return (
    <div className="block-palette-overlay" role="dialog" aria-modal="true" aria-label="Insert block">
      <button
        type="button"
        className="block-palette-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="block-palette" onKeyDown={onKeyDown}>
        <div className="block-palette-search">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search blocks…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
          />
          <button type="button" className="block-palette-close" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="block-palette-body">
          {grouped.length === 0 && (
            <p className="font-display-italic text-[1.05rem] text-[var(--color-ink-mute)] p-6">
              No blocks match "{query}".
            </p>
          )}
          {grouped.map(([group, items]) => (
            <section key={group} className="block-palette-group">
              <p className="block-palette-group-label">{GROUP_LABELS[group] ?? group}</p>
              <ul>
                {items.map((t) => {
                  const idx = filtered.indexOf(t);
                  return (
                    <li key={t.type}>
                      <button
                        type="button"
                        className={`block-palette-item ${idx === activeIdx ? "is-active" : ""}`}
                        onClick={() => onPick(t)}
                        onMouseEnter={() => setActiveIdx(idx)}
                      >
                        <span className="block-palette-icon" aria-hidden>
                          {t.icon}
                        </span>
                        <span className="block-palette-text">
                          <span className="block-palette-label">{t.label}</span>
                          {t.description && (
                            <span className="block-palette-desc">{t.description}</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
